import { useEffect, useRef } from "react";
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from "react";
import { t, useStoreLanguage, localizeDigits } from "../../../features/store-language/i18n.ts";
import { normalizeLoginDigits } from "./customerIdentityService.ts";

type AgencyVerificationCodeInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  focusRequest?: number | null;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
};

const CODE_LENGTH = 6;

export default function AgencyVerificationCodeInput({
  value = "",
  onChange,
  onComplete,
  disabled = false,
  focusRequest,
  id,
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
}: AgencyVerificationCodeInputProps) {
  useStoreLanguage();
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const focusedRequest = useRef<number | null>(null);

  useEffect(function () {
    if (focusRequest == null) {
      focusedRequest.current = null;
      return;
    }
    if (disabled || focusedRequest.current === focusRequest) return;
    inputs.current[0]?.focus();
    focusedRequest.current = focusRequest;
  }, [focusRequest, disabled]);

  // Preserve empty positions while editing so deleting one digit never moves the others.
  const digits = value.replace(/[۰-۹٠-٩]/g, function (digit) {
    return normalizeLoginDigits(digit);
  }).replace(/[^0-9 ]/g, "").slice(0, CODE_LENGTH);

  function clearDigit(index: number) {
    const next = digits.padEnd(CODE_LENGTH, " ").split("");
    next[index] = " ";
    onChange?.(next.join("").trimEnd());
  }

  function focusDigit(index: number) {
    inputs.current[index]?.focus();
    inputs.current[index]?.select();
  }

  function updateDigits(index: number, raw: string) {
    const incoming = normalizeLoginDigits(raw).replace(/\D/g, "");
    if (!incoming) return;
    const start = incoming.length >= CODE_LENGTH ? 0 : index;
    const next = digits.padEnd(CODE_LENGTH, " ").split("");
    incoming.slice(0, CODE_LENGTH - start).split("").forEach(function (digit, offset) {
      next[start + offset] = digit;
    });
    const result = next.join("").trimEnd();
    onChange?.(result);
    if (/^\d{6}$/.test(result) && result !== digits) onComplete?.(result);
    const nextIndex = Math.min(start + incoming.length, CODE_LENGTH - 1);
    if (!/^\d{6}$/.test(result)) {
      requestAnimationFrame(function () { focusDigit(nextIndex); });
    }
  }

  function handleChange(index: number, event: ChangeEvent<HTMLInputElement>) {
    if (event.target.value === "") {
      clearDigit(index);
      return;
    }
    updateDigits(index, event.target.value);
  }

  function handlePaste(index: number, event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    updateDigits(index, event.clipboardData.getData("text"));
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      const target = event.key === "Backspace" && !digits[index]?.trim() ? Math.max(0, index - 1) : index;
      clearDigit(target);
      requestAnimationFrame(function () { focusDigit(target); });
    } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      focusDigit(Math.max(0, Math.min(CODE_LENGTH - 1, index + (event.key === "ArrowRight" ? 1 : -1))));
    }
  }

  return (
    <div
      className="customer-invitation-otp"
      role="group"
      aria-label={t("کد تأیید شمارهٔ همراه")}
      dir="ltr"
    >
      {Array.from({ length: CODE_LENGTH }, function (_, index) {
        return (
          <input
            key={index}
            id={index === 0 ? id : undefined}
            ref={function (element) { inputs.current[index] = element; }}
            className={digits[index]?.trim() ? "is-filled" : undefined}
            type="text"
            inputMode="numeric"
            pattern="[0-9۰-۹٠-٩]*"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            aria-label={t("رقم {{value0}} از کد تأیید", { value0: index + 1 })}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={localizeDigits(digits[index]?.trim())}
            disabled={disabled}
            onChange={function (event) { handleChange(index, event); }}
            onFocus={function (event) {
              event.currentTarget.select();
            }}
            onClick={function (event) { event.currentTarget.select(); }}
            onPaste={function (event) { handlePaste(index, event); }}
            onKeyDown={function (event) { handleKeyDown(index, event); }}
          />
        );
      })}
    </div>
  );
}
